import type { ConversationListItem, LiveConversationStateT } from '@chatbot/shared-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type WsStatus, useGlobalWebSocket } from '../../hooks/WebSocketProvider.tsx';
import { conversationsApi } from '../../lib/conversations-api.ts';
import { sonarConversacionNueva, sonarEscalacion } from '../../lib/inbox-sound.ts';
import { toast } from '../Toast.tsx';

/**
 * Cuánto se espera antes de recargar la lista tras un mensaje.
 *
 * Los mensajes llegan en ráfagas —una persona manda tres seguidos, o entran
 * varias a la vez— y una recarga por mensaje le pega a la API sin cambiar nada
 * en pantalla. Medio segundo agrupa la ráfaga y sigue leyéndose como instantáneo.
 */
const AGRUPAR_MS = 500;

export type ConversationStateFilter = LiveConversationStateT | 'all';

export interface ConversationsListResult {
  /** Items ya filtrados por el texto de búsqueda (el filtro de estado va a la API). */
  items: ConversationListItem[];
  loading: boolean;
  error: string | null;
  wsStatus: WsStatus;
  refetch: () => Promise<void>;
}

/**
 * Carga la lista de conversaciones de un tenant y la mantiene viva.
 *
 * - `stateFilter` viaja a la API (`state`); 'all' omite el parámetro.
 * - `search` filtra en cliente para no pegarle a la API en cada tecla.
 * - Refetch reactivo por WebSocket + polling de 5s cuando el WS está degradado.
 *
 * Vive separado del hilo (useConversationThread) para que seleccionar una
 * conversación NO recargue la lista.
 */
export function useConversationsList(
  tenantId: string,
  stateFilter: ConversationStateFilter,
  search: string,
): ConversationsListResult {
  const [items, setItems] = useState<ConversationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { status: wsStatus, lastReconnectAt, subscribe } = useGlobalWebSocket();

  /**
   * Las conversaciones que ya estaban la última vez que se miró, junto con la
   * lista a la que pertenecen.
   *
   * Sirve para distinguir «llegó un mensaje de alguien conocido» de «apareció
   * alguien nuevo», que es lo único que hace sonar la bandeja. Va en un ref y no
   * en estado: cambiarlo no tiene que redibujar nada.
   *
   * La `clave` es el tenant y el filtro. Cambiar cualquiera de los dos cambia el
   * universo de conversaciones: lo que aparece no es gente nueva, es otra lista.
   * Guardarla acá —en vez de resetear el ref desde un efecto aparte— hace que la
   * comparación no pueda desincronizarse con la respuesta que está llegando.
   */
  const conocidas = useRef<{ clave: string; ids: Set<string> } | null>(null);

  /**
   * Una escalación ya avisó con su propio sonido; la recarga que viene no
   * vuelve a avisar.
   *
   * Con el filtro en «Esperando atención», el mismo hecho dispara los dos
   * caminos: el evento `conversation.escalated` y, un instante después, una
   * recarga que ve un identificador que no estaba en esa lista. Sin esto suena
   * dos veces —y con dos sonidos distintos—, que es peor que no sonar: el
   * operador aprende a desconfiar del aviso.
   */
  const yaAvisoLaEscalacion = useRef(false);

  /**
   * @param silencioso No toca `loading`. Las recargas disparadas por un mensaje
   * son de fondo: prender el spinner en cada una haría parpadear la lista
   * entera varias veces por minuto.
   */
  const cargar = useCallback(
    async (silencioso = false) => {
      if (!tenantId) {
        setItems([]);
        setLoading(false);
        return;
      }
      if (!silencioso) setLoading(true);
      setError(null);
      try {
        const res = await conversationsApi.list({
          tenantId,
          state: stateFilter === 'all' ? undefined : stateFilter,
        });

        const clave = `${tenantId}|${stateFilter}`;
        const ids = new Set(res.items.map((c) => c.userIdentifier));
        // Sólo hay con qué comparar si la carga anterior era de ESTA misma lista.
        // En la primera carga —y al cambiar de tenant o filtro— todas son nuevas;
        // sonar ahí sería un golpe de sonido al abrir el panel, no un aviso.
        const previas = conocidas.current?.clave === clave ? conocidas.current.ids : null;
        const hayAlguienNuevo =
          !!previas?.size && res.items.some((c) => !previas.has(c.userIdentifier));
        if (hayAlguienNuevo && !yaAvisoLaEscalacion.current) {
          sonarConversacionNueva();
        }
        // Se consume siempre, haya sonado o no: si no, queda armado y se come
        // el próximo aviso legítimo de alguien nuevo.
        yaAvisoLaEscalacion.current = false;
        conocidas.current = { clave, ids };

        setItems(res.items);
      } catch (err) {
        setError((err as Error).message);
        toast.error(`Error al cargar conversaciones: ${(err as Error).message}`);
      } finally {
        if (!silencioso) setLoading(false);
      }
    },
    [tenantId, stateFilter],
  );

  const refetch = useCallback(() => cargar(false), [cargar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Reconciliar el gap tras una reconexión del WebSocket
  useEffect(() => {
    if (lastReconnectAt) refetch();
  }, [lastReconnectAt, refetch]);

  useEffect(() => {
    if (!tenantId) return;
    let pendiente: ReturnType<typeof setTimeout> | undefined;

    const cancelar = subscribe((event) => {
      if (event.tenantId !== tenantId) return;

      // Un cambio de estado mueve la conversación de pestaña y el operador lo
      // está esperando: se recarga ya, y con spinner.
      // Una escalación avisa con SU sonido, y no depende del filtro activo: con
      // «Todas», esa persona ya estaba en la lista y la comparación de
      // identificadores nuevos no la ve. No es una conversación nueva, es una
      // que cambió de peso.
      if (event.type === 'conversation.escalated') {
        sonarEscalacion();
        yaAvisoLaEscalacion.current = true;
      }

      if (
        event.type === 'conversation.escalated' ||
        event.type === 'state.changed' ||
        event.type === 'admin.takeover'
      ) {
        refetch();
        return;
      }

      // Un mensaje entrante o saliente cambia la vista previa y el orden. Antes
      // este evento se ignoraba, y por eso la bandeja parecía congelada: las
      // conversaciones nuevas no aparecían hasta recargar la página.
      if (event.type === 'message.received' || event.type === 'message.sent') {
        clearTimeout(pendiente);
        pendiente = setTimeout(() => void cargar(true), AGRUPAR_MS);
      }
    });

    return () => {
      clearTimeout(pendiente);
      cancelar?.();
    };
  }, [tenantId, refetch, cargar, subscribe]);

  // Fallback de polling cuando el WebSocket se degrada
  useEffect(() => {
    if (wsStatus !== 'degraded') return;
    const interval = setInterval(refetch, 5000);
    return () => clearInterval(interval);
  }, [wsStatus, refetch]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter(
      (c) =>
        c.userIdentifier.toLowerCase().includes(query) ||
        c.userDisplayName?.toLowerCase().includes(query) ||
        c.lastMessagePreview?.toLowerCase().includes(query),
    );
  }, [items, search]);

  return { items: filtered, loading, error, wsStatus, refetch };
}
